import { bg } from 'lib/background'
import { debug, logWarn } from 'lib/logging'

const CHIMED_STORAGE_KEY = `trk:chimedAlertIds`
const CHIME_LOCK_NAME = `trk:alertChime`
// Long enough to span every tab's poll offset, short enough that the list stays tiny.
const CHIMED_TTL_MS = 10 * 60_000

// Keyed on the alert, not on time: each tab polls on its own clock, so the same
// alert reaches them seconds apart.
function claimAlerts(alertIds: string[]): boolean {
  try {
    const now = Date.now()
    const stored: Record<string, number> = JSON.parse(localStorage.getItem(CHIMED_STORAGE_KEY) ?? `{}`)
    const chimed = Object.fromEntries(Object.entries(stored).filter(([, at]) => now - at < CHIMED_TTL_MS))
    const unclaimed = alertIds.filter((id) => !(id in chimed))
    if (!unclaimed.length) return false
    for (const id of unclaimed) chimed[id] = now
    localStorage.setItem(CHIMED_STORAGE_KEY, JSON.stringify(chimed))
    return true
  } catch (err) {
    // Storage blocked or corrupt: a duplicate chime beats a missed one.
    logWarn(`Could not claim alert chime`, err)
    return true
  }
}

/**
 * Plays the alert chime through a caller-owned `<audio>` ref, so each alert
 * source keeps one element instead of allocating per event. Stays quiet when
 * another tab already chimed for every one of `alertIds`.
 */
export function playChime(ref: { current: HTMLAudioElement | undefined }, alertIds: string[]): void {
  const play = () => {
    if (!claimAlerts(alertIds)) return
    try {
      if (!ref.current) ref.current = new Audio(`/public/Notification.mp3`)
      ref.current.currentTime = 0
      // Browsers refuse audio until the tab has been interacted with. A tab that
      // was opened and never clicked will reject here; that is not an error worth
      // surfacing, so it stays a debug line.
      bg(ref.current.play().catch((err: unknown) => debug(`alerts`, `chime blocked`, err)))
    } catch (err) {
      logWarn(`Could not play alert chime`, err)
    }
  }
  // The lock serializes the read-then-write claim across tabs, so two tabs
  // polling in the same instant cannot both see the alert as unclaimed.
  if (typeof navigator !== `undefined` && navigator.locks) bg(navigator.locks.request(CHIME_LOCK_NAME, play))
  else play()
}
